/**
 * AvatarDropdown — shared authed-cluster trigger for every role shell.
 *
 * Phase 2 of the App Shell Redesign renames + refactors the legacy
 * `UserMenu`. The trigger is now an initials chip (slate `#374751`
 * circle, white initials, chevron) used at every viewport — no
 * text-name fallback. The dropdown body keeps the prior surfaces:
 *
 *   - "Signed in as" header (name + email)
 *   - Multi-role "Switch view" group (Admin / Customer / PooPartner)
 *     with `Shield` / `Home` / `PawPrint` icons. Active row gets an
 *     orange-tint background (`#FDEEDA`) + bold weight in addition to
 *     the existing right-side orange `Check`.
 *   - Single-role users skip the switcher — they see a direct
 *     dashboard link instead, plus an "Admin portal" jump when
 *     `isAdmin` and currently outside admin view.
 *   - Optional `extraMenuItems` slot (CustomerShell passes "Plans &
 *     pricing" + "About & help" — admin/provider shells pass nothing).
 *   - Item M: admin-only "View as sample" group with two
 *     quick-impersonate shortcuts (`user-menu-impersonate-sample-partner`
 *     and `user-menu-impersonate-sample-customer`). Each item resolves
 *     a stable demo `users.id` via `GET /api/admin/impersonation/demo-shortcuts`
 *     and triggers the EXISTING `POST /api/admin/impersonation/start`
 *     flow (same banner, same timer, same audit row). Items that fail
 *     to resolve (404, prod DB, etc.) are fully removed from the DOM —
 *     no disabled state, no tooltip. The whole section disappears when
 *     both targets are absent.
 *   - Log out (always last).
 *
 * Decision #19: multi-role users still see `extraMenuItems` (when
 * supplied by the shell) so customers-with-multiple-views don't lose
 * the marketing affordances when they switch into a non-customer
 * view that happens to also have them.
 *
 * All prior data-testids are preserved (`user-menu-trigger`,
 * `user-menu-view-*`, `user-menu-logout`, `user-menu-email`, etc.).
 * New testids: `avatar-chip-initials`, `user-menu-extra-{key}`.
 *
 * Decision #17: `showAdminBadge` is removed from the public API — the
 * orange `AdminViewBar` + initials chip together signal admin mode
 * now. Callers that previously passed the prop just drop it.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth, pathForView } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import {
  ChevronDown,
  Check,
  LogOut,
  Shield,
  Home,
  PawPrint,
  LayoutDashboard,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api, formatApiErrorDetail } from "@/lib/api";

// Item M — AvatarDropdown quick-impersonate shortcuts.
// Hardcoded reason strings interpolated into the existing
// `POST /api/admin/impersonation/start` audit trail so the row is
// distinguishable from manual-picker sessions. These literals are
// pinned by the AvatarDropdown Jest suite (`avatardropdown-impm-*`).
const IMPERSONATE_REASON_PARTNER  = "Quick demo via AvatarDropdown — sample partner";
const IMPERSONATE_REASON_CUSTOMER = "Quick demo via AvatarDropdown — sample customer";

// Switcher labels per spec (#12). PooPartner label is renameable via
// admin Platform Settings; pulled from ConfigContext below.
const VIEW_PATH_LABELS = {
  admin: "Admin portal",
  customer: "Customer dashboard",
  // provider rendered from config (renameable display label)
};

// Switcher icons per spec (#12). Customer = Home (NOT LayoutDashboard).
const VIEW_ICONS = {
  admin: Shield,
  customer: Home,
  provider: PawPrint,
};

// Active-row treatment per spec (#11): orange-tint background +
// bold text. The existing right-side orange Check stays as a
// secondary affordance for accessibility (screen readers / a11y
// users picking up either signal).
//
// Dispatch #4 — theme-flip fix: dark-mode --pp-slate resolves to
// #F9FAFB (used as body text on charcoal surfaces), so the
// pre-Phase-L hardcoded `#FDEEDA` tint here collided in dark
// (1.09:1). Backed by the theme-conditional
// `--pp-avatar-active-row-bg` token pair (light: #FDEEDA / dark:
// #7A5232 — semantically the "warm orange-shifted charcoal" tier
// tint). L14 pins AA on both themes.
const ACTIVE_ROW_BG = "var(--pp-avatar-active-row-bg)";

function initialsOf(user) {
  if (!user) return "??";
  const name = (user.name || "").trim();
  if (name) {
    const fromName = (name.match(/\b\w/g) || []).join("").slice(0, 2).toUpperCase();
    if (fromName) return fromName;
  }
  // Fall back to the first two alpha chars of the email local-part.
  const email = (user.email || "").split("@")[0] || "";
  const fromEmail = (email.match(/[A-Za-z]/g) || []).join("").slice(0, 2).toUpperCase();
  return fromEmail || "??";
}

export default function AvatarDropdown({ extraMenuItems = [] }) {
  const {
    user,
    logout,
    view,
    setView,
    availableViews,
    hasMultipleViews,
    isAdmin,
    refreshMe,
  } = useAuth();
  const { providerDisplayLabel } = useConfig();
  const navigate = useNavigate();

  // Item M — fetch demo-shortcut targets once on mount when the
  // signed-in user is an admin. Non-admins skip the fetch entirely
  // so we never leak the existence of demo accounts via a network
  // probe. Each entry stays `null` until the response lands; on
  // network error we leave both null (graceful hide).
  const [demoShortcuts, setDemoShortcuts] = useState({
    partner: null,
    customer: null,
  });
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/impersonation/demo-shortcuts");
        if (cancelled) return;
        setDemoShortcuts({
          partner:  data?.partner  || null,
          customer: data?.customer || null,
        });
      } catch {
        // Hide-on-degrade: any error → keep both null → section hidden.
        if (!cancelled) setDemoShortcuts({ partner: null, customer: null });
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const startImpersonation = async (target, reason) => {
    if (!target || impersonating) return;
    setImpersonating(true);
    try {
      const { data } = await api.post("/admin/impersonation/start", {
        target_user_id: target.user_id,
        reason,
      });
      await refreshMe();
      toast.success(`Now acting as ${data.session.target_email}`);
      navigate(data.redirect_to || "/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setImpersonating(false);
    }
  };

  // Defensive: shells only mount AvatarDropdown after auth is settled,
  // but if `user` is briefly null during re-hydration the chip should
  // render nothing rather than crash.
  if (!user) return null;

  const initials = initialsOf(user);
  const displayName = user.name || user.email || "Account";

  const labelFor = (v) =>
    v === "provider"
      ? `${providerDisplayLabel} dashboard`
      : VIEW_PATH_LABELS[v] || v;

  const goToView = (v) => {
    setView(v);
    navigate(pathForView(v));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const currentView = view || (availableViews && availableViews[0]) || null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full px-1.5 py-1 transition-colors hover:bg-pp-v3-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange focus-visible:ring-offset-2"
          data-testid="user-menu-trigger"
          aria-label={`Account menu — ${displayName}`}
        >
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[12px] font-bold tracking-wide"
            style={{
              backgroundColor: "var(--pp-avatar-chip-bg)",
              color: "var(--pp-avatar-chip-fg)",
            }}
            data-testid="avatar-chip-initials"
            aria-hidden="true"
          >
            {initials}
          </span>
          <ChevronDown className="h-4 w-4 text-pp-text-soft opacity-70" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="min-w-[260px] rounded-[14px] border border-pp-line bg-pp-v3-bg p-1 shadow-[var(--pp-shadow)]"
        data-testid="user-menu-content"
      >
        <DropdownMenuLabel
          className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-pp-text-soft"
          data-testid="user-menu-label"
        >
          Signed in as
        </DropdownMenuLabel>
        <div className="-mt-1 px-3 pb-2">
          <p className="truncate text-[14px] font-semibold text-pp-slate" data-testid="user-menu-name">
            {displayName}
          </p>
          <p className="truncate text-[12px] text-pp-text-soft" data-testid="user-menu-email">
            {user.email}
          </p>
        </div>
        <DropdownMenuSeparator className="bg-pp-line" />

        {hasMultipleViews && (
          <>
            <DropdownMenuLabel className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-pp-text-soft">
              Switch view
            </DropdownMenuLabel>
            {availableViews.map((v) => {
              const Icon = VIEW_ICONS[v] || LayoutDashboard;
              const active = v === currentView;
              return (
                <DropdownMenuItem
                  key={v}
                  onSelect={(e) => {
                    e.preventDefault();
                    goToView(v);
                  }}
                  className={cn(
                    "cursor-pointer rounded-[8px] px-3 py-2 text-[14px] flex items-center gap-2",
                    active
                      ? "font-bold text-pp-slate focus:text-pp-slate"
                      : "font-medium text-pp-slate focus:bg-pp-v3-surface focus:text-pp-slate"
                  )}
                  style={active ? { backgroundColor: ACTIVE_ROW_BG } : undefined}
                  data-testid={`user-menu-view-${v}`}
                >
                  <Icon className={cn("h-4 w-4", active ? "text-pp-orange" : "opacity-70")} aria-hidden="true" />
                  <span className="flex-1">{labelFor(v)}</span>
                  {active && (
                    <Check className="h-4 w-4 text-pp-orange" aria-hidden="true" />
                  )}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator className="bg-pp-line" />
          </>
        )}

        {!hasMultipleViews && currentView && (
          <>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                goToView(currentView);
              }}
              className="cursor-pointer rounded-[8px] px-3 py-2 text-[14px] font-medium text-pp-slate focus:bg-pp-v3-surface focus:text-pp-slate flex items-center gap-2"
              data-testid="user-menu-dashboard"
            >
              {(() => {
                const Icon = VIEW_ICONS[currentView] || LayoutDashboard;
                return <Icon className="h-4 w-4 opacity-70" aria-hidden="true" />;
              })()}
              {labelFor(currentView)}
            </DropdownMenuItem>
            {isAdmin && currentView !== "admin" && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  navigate("/admin");
                }}
                className="cursor-pointer rounded-[8px] px-3 py-2 text-[14px] font-medium text-pp-slate focus:bg-pp-v3-surface focus:text-pp-slate flex items-center gap-2"
                data-testid="user-menu-admin-jump"
              >
                <Shield className="h-4 w-4 opacity-70" aria-hidden="true" />
                Admin portal
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-pp-line" />
          </>
        )}

        {extraMenuItems.length > 0 && (
          <>
            {extraMenuItems.map((item) => {
              const Icon = item.icon || null;
              return (
                <DropdownMenuItem
                  key={item.key || item.label}
                  onSelect={(e) => {
                    e.preventDefault();
                    if (item.onSelect) item.onSelect();
                    else if (item.to) navigate(item.to);
                  }}
                  className="cursor-pointer rounded-[8px] px-3 py-2 text-[14px] font-medium text-pp-slate focus:bg-pp-v3-surface focus:text-pp-slate flex items-center gap-2"
                  data-testid={`user-menu-extra-${item.key || item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {Icon && <Icon className="h-4 w-4 opacity-70" aria-hidden="true" />}
                  {item.label}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator className="bg-pp-line" />
          </>
        )}

        {isAdmin && (demoShortcuts.partner || demoShortcuts.customer) && (
          <div data-testid="user-menu-view-as-sample-section">
            <DropdownMenuLabel
              className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-pp-text-soft"
              data-testid="user-menu-view-as-sample-label"
            >
              View as sample
            </DropdownMenuLabel>
            {demoShortcuts.partner && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  startImpersonation(demoShortcuts.partner, IMPERSONATE_REASON_PARTNER);
                }}
                disabled={impersonating}
                className="cursor-pointer rounded-[8px] px-3 py-2 text-[14px] font-medium text-pp-slate focus:bg-pp-v3-surface focus:text-pp-slate flex items-center gap-2"
                data-testid="user-menu-impersonate-sample-partner"
              >
                <Eye className="h-4 w-4 opacity-70" aria-hidden="true" />
                View as sample {providerDisplayLabel}
              </DropdownMenuItem>
            )}
            {demoShortcuts.customer && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  startImpersonation(demoShortcuts.customer, IMPERSONATE_REASON_CUSTOMER);
                }}
                disabled={impersonating}
                className="cursor-pointer rounded-[8px] px-3 py-2 text-[14px] font-medium text-pp-slate focus:bg-pp-v3-surface focus:text-pp-slate flex items-center gap-2"
                data-testid="user-menu-impersonate-sample-customer"
              >
                <Eye className="h-4 w-4 opacity-70" aria-hidden="true" />
                View as sample Customer
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-pp-line" />
          </div>
        )}

        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            handleLogout();
          }}
          className="cursor-pointer rounded-[8px] px-3 py-2 text-[14px] font-medium text-pp-slate focus:bg-pp-v3-surface focus:text-pp-slate flex items-center gap-2"
          data-testid="user-menu-logout"
        >
          <LogOut className="h-4 w-4 opacity-70" aria-hidden="true" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
